import React from 'react';
import { motion } from 'framer-motion';
/**
 * FlowchartVisual — STU-FLOWCHART visual aid
 *
 * Renders a process flowchart with 4 node shapes:
 *   - 'start' / 'end'  → Ovaal (afgeronde rechthoek)
 *   - 'action'         → Rechthoek
 *   - 'decision'       → Ruit (diamant)
 *   - 'connector'      → Kleine cirkel (optioneel)
 *
 * Connections are auto-drawn as arrows tussen nodes in volgorde (sequential
 * chaining) plus optionele explicit edges met labels (ja/nee) voor beslissingen.
 *
 * Data shape:
 * {
 *   nodes: Array<{
 *     id: string
 *     shape: 'start' | 'end' | 'action' | 'decision'
 *     label: string
 *     highlight?: boolean
 *   }>
 *   edges?: Array<{ from: string; to: string; label?: string }>
 *   label?: string
 *   mode?: 'stimulus' | 'support'
 * }
 *
 * Als `edges` ontbreekt, worden nodes sequentieel verbonden (node[i] → node[i+1]).
 */
type FlowNodeShape = 'start' | 'end' | 'action' | 'decision';
interface FlowNode {
  id: string;
  shape: FlowNodeShape;
  label: string;
  highlight?: boolean;
}
interface FlowEdge {
  from: string;
  to: string;
  label?: string;
}
interface FlowchartVisualProps {
  nodes?: FlowNode[];
  edges?: FlowEdge[];
  label?: string;
  mode?: 'stimulus' | 'support';
}
// Layout config
const NODE_WIDTH = 160;
const NODE_HEIGHT = 60;
const DECISION_SIZE = 90; // diamond half-diagonal
const V_GAP = 50;
const SIDE_PADDING = 24;
function shapeColors(shape: FlowNodeShape, highlighted: boolean) {
  if (highlighted) {
    return {
      fill: '#fde68a',
      stroke: '#f59e0b',
      text: '#78350f' // amber-900
    };
  }
  switch (shape) {
    case 'start':
      return {
        fill: '#dcfce7',
        stroke: '#22c55e',
        text: '#14532d'
      };
    // green
    case 'end':
      return {
        fill: '#fee2e2',
        stroke: '#ef4444',
        text: '#7f1d1d'
      };
    // red
    case 'decision':
      return {
        fill: '#fef3c7',
        stroke: '#f59e0b',
        text: '#78350f'
      };
    // amber
    case 'action':
    default:
      return {
        fill: '#e0e7ff',
        stroke: '#6366f1',
        text: '#312e81'
      };
    // indigo
  }
}
interface PlacedNode extends FlowNode {
  x: number; // center x
  y: number; // center y
  w: number;
  h: number;
}
export function FlowchartVisual({
  nodes = [],
  edges,
  label,
  mode = 'stimulus'
}: FlowchartVisualProps) {
  if (!Array.isArray(nodes) || nodes.length === 0) {
    return <div className="rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-500" data-id="element-11644">
        Flowchart kan niet worden getoond (geen nodes).
      </div>;
  }
  const isStimulus = mode === 'stimulus';
  // ── Edges: explicit if provided, else sequential ──
  const allEdges: FlowEdge[] = Array.isArray(edges) && edges.length > 0 ? edges : nodes.slice(0, -1).map((n, i) => ({
    from: n.id,
    to: nodes[i + 1].id
  }));
  // ── Layout: 2D level-based branching layout ──
  const adj = new Map<string, string[]>();
  nodes.forEach(n => adj.set(n.id, []));
  allEdges.forEach(e => {
    if (adj.has(e.from)) adj.get(e.from)!.push(e.to);
  });
  const inDegree = new Map<string, number>();
  nodes.forEach(n => inDegree.set(n.id, 0));
  allEdges.forEach(e => {
    if (inDegree.has(e.to)) inDegree.set(e.to, inDegree.get(e.to)! + 1);
  });
  let roots = nodes.filter(n => inDegree.get(n.id) === 0);
  if (roots.length === 0 && nodes.length > 0) roots = [nodes[0]];
  const levels = new Map<string, number>();
  const queue = roots.map(r => ({
    id: r.id,
    level: 0
  }));
  let maxLevel = 0;
  while (queue.length > 0) {
    const {
      id,
      level
    } = queue.shift()!;
    if (!levels.has(id) || levels.get(id)! < level) {
      levels.set(id, level);
      maxLevel = Math.max(maxLevel, level);
      const children = adj.get(id) || [];
      children.forEach(child => queue.push({
        id: child,
        level: level + 1
      }));
    }
  }
  nodes.forEach(n => {
    if (!levels.has(n.id)) levels.set(n.id, 0);
  });
  const levelGroups: FlowNode[][] = Array.from({
    length: maxLevel + 1
  }, () => []);
  nodes.forEach(n => {
    levelGroups[levels.get(n.id)!].push(n);
  });
  const H_GAP = 40;
  const maxNodesInLevel = Math.max(...levelGroups.map(g => g.length));
  const svgWidth = Math.max(SIDE_PADDING * 2 + maxNodesInLevel * NODE_WIDTH + (maxNodesInLevel - 1) * H_GAP, 300);
  const placed: PlacedNode[] = [];
  levelGroups.forEach((group, levelIdx) => {
    const groupWidth = group.length * NODE_WIDTH + (group.length - 1) * H_GAP;
    let startX = (svgWidth - groupWidth) / 2 + NODE_WIDTH / 2;
    group.forEach(n => {
      const isDecision = n.shape === 'decision';
      placed.push({
        ...n,
        x: startX,
        y: SIDE_PADDING + NODE_HEIGHT / 2 + levelIdx * (NODE_HEIGHT + V_GAP) + (isDecision ? 10 : 0),
        w: isDecision ? DECISION_SIZE * 2 : NODE_WIDTH,
        h: isDecision ? DECISION_SIZE * 2 : NODE_HEIGHT
      });
      startX += NODE_WIDTH + H_GAP;
    });
  });
  const lastLevelY = Math.max(...placed.map(n => n.y + n.h / 2));
  const svgHeight = lastLevelY + SIDE_PADDING + 20;
  const findNode = (id: string) => placed.find(n => n.id === id);
  return <figure className={`flex flex-col items-center gap-2 rounded-xl border-2 ${isStimulus ? 'border-indigo-200 bg-indigo-50/40' : 'border-indigo-300 bg-gradient-to-br from-indigo-50 to-violet-50 shadow-sm'} p-4`} aria-label={label ? `Flowchart: ${label}` : 'Flowchart'} data-id="element-11645">
      {label && <figcaption className="text-sm font-semibold text-indigo-800" data-id="element-11646">
          {label}
        </figcaption>}

      <svg width="100%" viewBox={`0 0 ${svgWidth} ${svgHeight}`} style={{
      maxWidth: `${svgWidth}px`,
      height: 'auto'
    }} role="img" aria-hidden="false" data-id="element-11647">
        <defs data-id="element-11648">
          <marker id="flowchart-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse" data-id="element-11649">
            <path d="M 0 0 L 10 5 L 0 10 z" fill="#64748b" data-id="element-11650" />
          </marker>
        </defs>

        {/* Edges (drawn first, behind nodes) */}
        {allEdges.map((edge, i) => {
        const from = findNode(edge.from);
        const to = findNode(edge.to);
        if (!from || !to) return null;
        let x1 = from.x;
        let y1 = from.y;
        let x2 = to.x;
        let y2 = to.y;
        if (to.y > from.y + from.h / 2) {
          // to is below from
          y1 = from.y + from.h / 2;
          y2 = to.y - to.h / 2 - 4;
        } else if (to.y < from.y - from.h / 2) {
          // to is above from
          y1 = from.y - from.h / 2;
          y2 = to.y + to.h / 2 + 4;
        } else {
          // same level
          if (to.x > from.x) {
            x1 = from.x + from.w / 2;
            x2 = to.x - to.w / 2 - 4;
          } else {
            x1 = from.x - from.w / 2;
            x2 = to.x + to.w / 2 + 4;
          }
        }
        const midX = (x1 + x2) / 2;
        const midY = (y1 + y2) / 2;
        return <g key={`edge-${i}`} data-id="element-11651">
              <motion.line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#64748b" strokeWidth={2} markerEnd="url(#flowchart-arrow)" initial={{
            pathLength: 0,
            opacity: 0
          }} animate={{
            pathLength: 1,
            opacity: 1
          }} transition={{
            delay: 0.1 + i * 0.1,
            duration: 0.4
          }} data-id="element-11652" />
              {edge.label && <motion.g initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.3 + i * 0.1,
            duration: 0.3
          }} data-id="element-11653">
                  <rect x={midX - (edge.label.length * 3.5 + 6)} y={midY - 10} width={edge.label.length * 7 + 12} height={18} rx={4} fill="white" stroke="#cbd5e1" data-id="element-11654" />
                  <text x={midX} y={midY + 3} fontSize={11} fill="#475569" fontWeight={600} textAnchor="middle" data-id="element-11655">
                    {edge.label}
                  </text>
                </motion.g>}
            </g>;
      })}

        {/* Nodes */}
        {placed.map((n, i) => {
        const colors = shapeColors(n.shape, !!n.highlight);
        const isDecision = n.shape === 'decision';
        const isOval = n.shape === 'start' || n.shape === 'end';
        return <motion.g key={n.id} initial={{
          opacity: 0,
          scale: 0.85
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: i * 0.1,
          type: 'spring',
          stiffness: 280,
          damping: 20
        }} data-id="element-11656">
              {isDecision ?
          // Diamond (ruit)
          <polygon points={`
                    ${n.x},${n.y - DECISION_SIZE}
                    ${n.x + DECISION_SIZE},${n.y}
                    ${n.x},${n.y + DECISION_SIZE}
                    ${n.x - DECISION_SIZE},${n.y}
                  `} fill={colors.fill} stroke={colors.stroke} strokeWidth={n.highlight ? 3 : 2} data-id="element-11657" /> :
          // Rectangle or Oval
          <rect x={n.x - NODE_WIDTH / 2} y={n.y - NODE_HEIGHT / 2} width={NODE_WIDTH} height={NODE_HEIGHT} rx={isOval ? NODE_HEIGHT / 2 : 8} fill={colors.fill} stroke={colors.stroke} strokeWidth={n.highlight ? 3 : 2} data-id="element-11658" />}

              {/* Label text — wrap to two lines if needed */}
              <foreignObject x={n.x - (isDecision ? DECISION_SIZE - 12 : NODE_WIDTH / 2 - 8)} y={n.y - (isDecision ? DECISION_SIZE / 2 - 4 : NODE_HEIGHT / 2 - 4)} width={isDecision ? (DECISION_SIZE - 12) * 2 : NODE_WIDTH - 16} height={isDecision ? DECISION_SIZE - 8 : NODE_HEIGHT - 8} data-id="element-11659">
                <div style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              fontSize: isDecision ? 12 : 13,
              fontWeight: 600,
              color: colors.text,
              lineHeight: 1.2,
              padding: '0 4px'
            }} data-id="element-11660">
                  {n.label}
                </div>
              </foreignObject>
            </motion.g>;
      })}
      </svg>

      {/* Legenda — only in support mode */}
      {!isStimulus && <div className="mt-1 flex flex-wrap items-center justify-center gap-3 text-xs text-slate-600" data-id="element-11661">
          <LegendItem color="#22c55e" label="Ovaal = begin/einde" data-id="element-11662" />
          <LegendItem color="#6366f1" label="Rechthoek = actie" data-id="element-11663" />
          <LegendItem color="#f59e0b" label="Ruit = beslissing" data-id="element-11664" />
        </div>}
    </figure>;
}
function LegendItem({
  color,
  label
}: {
  color: string;
  label: string;
}) {
  return <span className="inline-flex items-center gap-1.5" data-id="element-11665">
      <span className="inline-block h-3 w-3 rounded-sm border" style={{
      backgroundColor: color + '33',
      borderColor: color
    }} data-id="element-11666" />
      {label}
    </span>;
}
export default FlowchartVisual;